import React, { useState } from 'react';
import { useAdminRoles } from '../../hooks/useAdminRoles';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import { RoleTable } from '../../components/Admin/RoleTable';
import { Role, Permission } from '../../types/admin';

export const RolesManagement: React.FC = () => {
  const { roles, loading, error, deleteRole, updateRolePermissions } = useAdminRoles();
  const { permissions } = useAdminPermissions();
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleSelectRole = (role: Role) => {
    setSelectedRole(role);
    // Extract permission IDs from the nested structure
    // The API returns permissions as an array of RolePermission objects with nested permission data
    const permIds = role.permissions?.map(p => {
      // Handle both direct Permission objects and nested RolePermission objects
      return (p as any).permission?.id || (p as any).id;
    }) || [];
    setSelectedPermissions(permIds);
  };

  const handlePermissionToggle = (permissionId: string) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;

    try {
      setIsSaving(true);
      await updateRolePermissions(selectedRole.id, {
        permissionIds: selectedPermissions,
      });
      alert('Role permissions updated successfully!');
    } catch (err) {
      console.error('Failed to update role permissions:', err);
      alert('Failed to update role permissions');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRole = async (role: Role) => {
    if (window.confirm(`Are you sure you want to delete the ${role.name} role?`)) {
      try {
        await deleteRole(role.id);
        setSelectedRole(null);
        alert('Role deleted successfully!');
      } catch (err) {
        console.error('Failed to delete role:', err);
      }
    }
  };

  // Group permissions by page
  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.page]) {
      acc[perm.page] = [];
    }
    acc[perm.page].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Roles Management</h1>
      </div>

      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Roles List */}
        <div className="col-span-2 bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Roles List ({roles.length})</h2>
          <RoleTable
            roles={roles}
            onEdit={handleSelectRole}
            onDelete={handleDeleteRole}
            loading={loading}
          />
        </div>

        {/* Role Details */}
        <div className="bg-white p-6 rounded-lg shadow">
          {selectedRole ? (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedRole.name}</h3>
                <p className="text-sm text-gray-600">{selectedRole.description}</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Permissions</h4>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {Object.entries(groupedPermissions).map(([page, perms]) => (
                    <div key={page}>
                      <p className="text-xs font-semibold text-gray-600 uppercase">{page}</p>
                      <div className="ml-2 space-y-1">
                        {perms.map(perm => (
                          <label key={perm.id} className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              checked={selectedPermissions.includes(perm.id)}
                              onChange={() => handlePermissionToggle(perm.id)}
                              className="mr-2"
                            />
                            {perm.action}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <button
                  onClick={handleSavePermissions}
                  disabled={isSaving}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {isSaving ? 'Saving...' : 'Save Permissions'}
                </button>
                <button
                  onClick={() => setSelectedRole(null)}
                  className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              Select a role to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

